/**
 * Iter-127 — In-memory SEO override registry.
 *
 * Admin can override `(path → title, description, og_image, canonical)`
 * for any non-guide page via `/admin?tab=seo`. The `<SEO>` component
 * calls `getSeoOverride(path)` at render and replaces the JSX-defined
 * defaults when an override row exists.
 *
 * Design notes:
 *   - One-shot fetch at boot (cheap — <50 rows in practice).
 *   - Subsequent calls read from the in-memory map (no per-render
 *     network round-trip).
 *   - Subscribers can `subscribeSeoOverrides(cb)` to re-render when
 *     the map mutates (used by the admin Preview pane).
 *   - Safe in SSR: returns `{}` when window is undefined.
 */
const cache = new Map();
const subscribers = new Set();
let hydrated = false;

function notify() {
  for (const cb of subscribers) {
    try {
      cb();
    } catch {
      /* swallow */
    }
  }
}

export function getSeoOverride(path) {
  if (!path) return null;
  return cache.get(path) || null;
}

/**
 * Iter-222 — Convenience accessor for the on-page H1 override.
 * Returns the override row's `h1` string or `null` when no override
 * exists (or the row was saved without an H1). Pages that wish to
 * honour an admin-set H1 can call:
 *
 *   const h1Override = getH1Override("/auto-insurance");
 *   const heading = h1Override || "Find the right auto insurance…";
 *
 * Keeping it as a thin pure helper avoids forcing every page through a
 * React hook just to read a static string.
 */
export function getH1Override(path) {
  const row = getSeoOverride(path);
  if (!row) return null;
  const h1 = (row.h1 || "").trim();
  return h1 || null;
}

export function setSeoOverride(row) {
  if (!row || !row.path) return;
  cache.set(row.path, row);
  notify();
}

export function clearSeoOverride(path) {
  cache.delete(path);
  notify();
}

export function subscribeSeoOverrides(cb) {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

export async function hydrateSeoOverrides() {
  if (typeof window === "undefined") return;
  if (hydrated) return;
  hydrated = true;
  if (!process.env.REACT_APP_BACKEND_URL) return;
  const base = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
  try {
    const rows = await fetch(`${base}/api/seo-overrides`).then((r) =>
      r.ok ? r.json() : [],
    );
    if (Array.isArray(rows)) {
      rows.forEach((row) => {
        if (row?.path) cache.set(row.path, row);
      });
      notify();
    }
  } catch {
    /* fall back to inline JSX defaults */
  }
}
